<template>
  <div class="CoverageFormat">
    <Helmet>
      <title>Internet in Bhutan - inte-QT</title>
      <meta name="title" content="Internet in Bhutan - inte-QT" />
      <meta
        name="description"
        content="Bhutan offers reliable mobile broadband with extensive 4G LTE coverage. inte-QT provides premium Dedicated Internet Access (DIA) and business broadband services."
      />
      <meta
        name="keywords"
        content="internet in Bhutan, broadband Bhutan, 4G Bhutan, telecom Bhutan, ICT Bhutan"
      />
      <link rel="canonical" href="https://www.inte-qt.com/coverage/bhutan" />
    </Helmet>

    <img
      src="/images/8DtjWMF.jpg"
      alt="Bhimakali Temple in Bhutan"
      class="banner-img CoverageFormat__saturation"
    />

    <section class="mt-5">
      <nav class="container">
        <ul>
          <li><strong>Population:</strong> 787,000 (2023 estimate)</li>
          <li><strong>Neighbor Countries:</strong> China (north) and India (south, east, and west). Landlocked country in the Eastern Himalayas.</li>
          <li><strong>Language Spoken:</strong> Official language: Dzongkha. Other languages include Nepali, Sharchhop, and various regional dialects.</li>
          <li><strong>Currency:</strong> Bhutanese Ngultrum (BTN) – pegged at 1:1 with the Indian Rupee (INR)</li>
          <li><strong>Industries:</strong> Hydropower, Tourism (especially eco and cultural tourism), Agriculture, Forestry, Handicrafts, and Cement.</li>
          <li><strong>Employment:</strong> Major sectors include agriculture (engaging over 50% of the workforce), government, hydropower projects, and services.</li>
          <li><strong>Top 3 Cities:</strong> Thimphu (capital and largest city), Phuentsholing, Paro</li>
          <li><strong>Tourism:</strong> Around 315,000 in 2019 (pre-pandemic); regulated tourism with daily tariff for sustainability.</li>
          <li><strong>Weather:</strong> Varies by altitude:
            <ul>
              <li>Subtropical in the south</li>
              <li>Temperate in the central valleys</li>
              <li>Cold alpine conditions in the north</li>
            </ul>
            Best travel months: March–May and September–November.
          </li>
          <li><strong>Sports:</strong> Archery is the national sport and deeply rooted in culture. Football, basketball, and cricket are also popular.</li>
        </ul>

        <div class="text-center">
          <img
            src="/images/xWYx5pC.jpg"
            alt="Bhutan Flag"
            class="my-5"
            style="width: 150px"
          />
        </div>
      </nav>

      <article>
        <h2>A Brief Overview of Bhutan</h2>
        <p>
          Bhutan is a small, landlocked country nestled in the Eastern Himalayas, known for its unique blend of culture, spirituality, and commitment to environmental sustainability. With a population of just under 800,000, Bhutan is a peaceful nation that has made eco-tourism a cornerstone of its economy. The government has been able to strike a delicate balance between modernization and maintaining its cultural heritage.
        </p>
        <p>
          Bhutan is renowned for its hydropower sector, which plays a significant role in its economy, alongside tourism, agriculture, and traditional handicrafts. Bhutan has been embracing digital transformation with improved mobile broadband coverage and international connectivity.
        </p>

        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d244205.2302803562!2d90.4120!3d27.5143!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3904c8c8e56e4fbf%3A0x5a7c255e1b5c96b4!2sBhutan!5e0!3m2!1sen!2sus!4v1683024072128"
          width="95%"
          height="450"
          style="border: 0"
          allowfullscreen=""
          loading="lazy"
          title="Bhutan Map"
          class="CoverageFormat__margins"
        ></iframe>

        <h4>Main Ports and Airports</h4>
        <ul>
          <li><strong>No seaports:</strong> Bhutan is landlocked</li>
          <li><strong>Major Airport:</strong> Paro International Airport (only international airport)</li>
        </ul>

        <h4>Typical Bhutanese Cuisine</h4>
        <ul>
          <li>Ema Datshi (chili and cheese stew, national dish)</li>
          <li>Red Rice</li>
          <li>Phaksha Paa (pork with chili and radish)</li>
          <li>Momos (dumplings)</li>
        </ul>
      </article>
    </section>

    <div class="container">
      <h4>Connectivity Overview</h4>
      <table class="table table-striped table-responsive">
        <thead>
          <tr>
            <th>Type</th>
            <th>Estimated Users</th>
            <th>Penetration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Landline Users</td>
            <td>Not specified</td>
            <td>-</td>
          </tr>
          <tr>
            <td>Mobile Cellular Users</td>
            <td>750,000 active connections</td>
            <td>97.3% of the population</td>
          </tr>
          <tr>
            <td>4G Subscribers</td>
            <td>Over 99% coverage</td>
            <td>Widely used for mobile broadband</td>
          </tr>
        </tbody>
      </table>

      <h4>Internet Providers in Bhutan</h4>
      <p>
        The primary Internet Service Providers (ISPs) in Bhutan include:
        <ul>
          <li>Bhutan Telecom Ltd (DrukNet): Offers a range of services, including mobile and fixed broadband</li>
          <li>Tashi InfoComm Limited (TashiCell): Provides mobile internet services with extensive 4G coverage</li>
          <li>NANO: Offers mobile internet services with growing 4G infrastructure</li>
        </ul>
        These providers offer various internet plans, and the demand for Dedicated Internet Access (DIA) services for businesses, particularly in sectors such as finance, IT, and government, is on the rise.
      </p>

      <h5>Our Capabilities</h5>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Capabilities</th>
            <th><i class="fas fa-check"></i></th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Dedicated Lines</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>Business Broadband</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>Wireless Connectivity</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>SLA</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>CPE / Router</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>GEMS</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>Diverse Gateway Solutions</td><td><i class="fas fa-check"></i></td></tr>
          <tr><td>DDoS Protection</td><td><i class="fas fa-check"></i></td></tr>
        </tbody>
      </table>

      <p class="CoverageFormat__tag CoverageFormat__margins" align="justify">
        Contact us to receive a <mark>Commercial Offer in 2–4 working days</mark>. Services are delivered within 3–4 weeks in Thimphu, Phuentsholing, Paro, and other major cities.
      </p>

      <p class="text-center">
        For more information, please <a href="mailto:sales@inte-QT.com?subject=Mail from Our Site">Email Us</a>.
      </p>

      <h4>* - References</h4>
      <ul>
        <li><a href="https://data.worldbank.org/country/bhutan" target="_blank" rel="noopener noreferrer">World Bank - Bhutan</a></li>
        <li><a href="https://www.bhutan.travel" target="_blank" rel="noopener noreferrer">Tourism Council of Bhutan</a></li>
        <li><a href="https://www.bt.bt" target="_blank" rel="noopener noreferrer">Bhutan Telecom</a></li>
        <li><a href="https://www.bicma.gov.bt" target="_blank" rel="noopener noreferrer">Bhutan InfoComm and Media Authority (BICMA)</a></li>
        <li><a href="https://www.submarinecablemap.com" target="_blank" rel="noopener noreferrer">Submarine Cable Map (for regional cable routes via India)</a></li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "BhutanPage",
};
</script>

<style scoped>
    .CoverageFormat {
      color: black;
    }

    .CoverageFormat__title {
      position: absolute;
      width: 50vw;
      top: 15vh;
      left: 25vw;
      line-height: 1.5;
      font-size: 3rem;
      font-weight: 500; 
      color: #ffffff;  
    }

    @media (max-width: 992px) {
      .CoverageFormat__title {
        font-size: 3rem;
        width: 40vw;
        top: 30vh;
        left: 55vw;
      }
    }

    @media (max-width: 768px) {
      .CoverageFormat__title {
        font-size: 2rem;
        width: 40vw;
        top: 25vh;
        left: 55vw;
      }
    }

    @media (max-width: 576px) {
      .CoverageFormat__title {
        top: 12vh;
        left: 5vw;
        width: 90vw;
        font-size: 1.5rem;
      }
    }

    .CoverageFormat__tag {
      font-size: 1.5rem;
    }

    .CoverageFormat section {
      display: -webkit-flex;
      display: flex;
    }

    .CoverageFormat nav {
      -webkit-flex: 1;
      -ms-flex: 1;
      flex: 1;
    }

    .CoverageFormat nav ul {
      padding: 0;
    }

    .CoverageFormat ul li,
    .CoverageFormat ol li {
      margin: 1rem;
      text-align: left;
    }

    .CoverageFormat article {
      color: black;
      -webkit-flex: 3;
      -ms-flex: 3;
      flex: 3;
      padding: 10px;
    }

    .CoverageFormat__saturation {
      filter: drop-shadow(1px 10px 40px rgb(0, 0, 0)) saturate(110%);
    }

    .CoverageFormat__margins {
      margin: 3rem;
    }

    @media (max-width: 768px) {
      .CoverageFormat__margins {
        margin: 1rem 0 1rem 0;
        text-align: left;
      }
    }

    .CoverageFormat__flag {
      width: 150px;
      border: 1px solid black;
    }

    @media (max-width: 768px) {
      .CoverageFormat section {
        -webkit-flex-direction: column;
        flex-direction: column;
      }
    }
  </style>
